import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import {
  InstallmentStatus,
  InvoiceStatus,
  Order,
  OrderPaymentInstallment,
  OrderProduct,
} from '../../../../../../../models/order.model';
import { AppTabsModule } from '../../../../../../../../shared/components/tabs/tabs.module';
import { OrderDetailsDataCardComponent } from '../order-details-data-card/order-details-data-card.component';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { OrderService } from '../../../../../../../services/order.service';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { OrderInstallmentPaymentsDialogComponent } from '../order-installment-payments-dialog/order-installment-payments-dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { isOrderCancelled } from '../../../../utils/order-details.util';

@Component({
  selector: 'app-order-installments',
  imports: [
    PaginatorModule,
    AppTabsModule,
    CurrencyPipe,
    DatePipe,
    OrderDetailsDataCardComponent,
    ButtonModule,
    OrderInstallmentPaymentsDialogComponent,
    DecimalPipe,
    TooltipModule,
  ],
  templateUrl: './order-installments.component.html',
})
export class OrderInstallmentsComponent {
  order = input<Order | null>(null);
  product = input<OrderProduct | null>(null);
  @Output() reloadOrder = new EventEmitter<void>();
  @Output() setLoading = new EventEmitter<boolean>();

  private orderService = inject(OrderService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  isCancelled = computed(() => isOrderCancelled(this.order()));

  allInstallments = computed(() => {
    const installments = this.order()?.paymentPlan.installments;
    if (!installments) return [];

    return installments
      .filter((x) => x.number > 0)
      .sort((a, b) => a.number - b.number);
  });

  installments = computed(() => {
    if (!this.order()?.paymentPlan.installments) return [];

    return this.order()!.paymentPlan.installments.filter(
      (x) => x.status !== InstallmentStatus.Paid && x.number > 0,
    );
  });

  paidInstallments = computed(() => {
    if (!this.order()?.paymentPlan.installments) return [];

    return this.order()!
      .paymentPlan.installments.filter(
        (x) => x.status === InstallmentStatus.Paid && x.number > 0,
      )
      .sort((a, b) => b.number - a.number);
  });

  readonly first = signal(0); // index of first record
  readonly rows = signal(5);

  readonly paidFirst = signal(0); // index of first record
  readonly paidRows = signal(5);
  readonly allFirst = signal(0);
  readonly allRows = signal(5);
  readonly chargingInstallmentId = signal<string | null>(null);

  readonly pagedInstallments = computed(() => {
    const list = this.installments() ?? [];
    const start = this.first();
    const end = start + this.rows();
    return list.slice(start, end);
  });

  readonly pagedAllInstallments = computed(() => {
    const start = this.allFirst();
    return this.allInstallments().slice(start, start + this.allRows());
  });

  readonly pagedPaidInstallments = computed(() => {
    const list = this.paidInstallments() ?? [];
    const start = this.paidFirst();
    const end = start + this.paidRows();
    return list.slice(start, end);
  });

  installmentPaymentDialogVisible = false;

  paymentInstallment: OrderPaymentInstallment | null = null;

  onPageChange(event: PaginatorState): void {
    this.first.set(event.first ?? 0);
    this.rows.set(event.rows ?? 10);
  }

  onAllPageChange(event: PaginatorState): void {
    this.allFirst.set(event.first ?? 0);
    this.allRows.set(event.rows ?? 10);
  }

  onPaidPageChange(event: PaginatorState): void {
    this.paidFirst.set(event.first ?? 0);
    this.paidRows.set(event.rows ?? 10);
  }

  getInstallmentStatusComponent(status: InstallmentStatus): string {
    return this.orderService.getInstallmentStatusComponent(status);
  }

  isPaidInstallment(installment: OrderPaymentInstallment): boolean {
    return installment.status === InstallmentStatus.Paid;
  }

  isChargeableInstallment(installment: OrderPaymentInstallment): boolean {
    return installment.status === InstallmentStatus.Overdue;
  }

  requestChargeInstallment(
    installment: OrderPaymentInstallment,
    event: Event,
  ): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: 'Você tem certeza que deseja cobrar esta parcela do cliente?',
      header: 'Confirmar cobrança',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Cobrar',
      },
      accept: () => {
        this.chargeInstallment(installment.id);
      },
    });
  }

  private chargeInstallment(installmentId: string): void {
    this.chargingInstallmentId.set(installmentId);
    this.setLoading.emit(true);

    this.orderService.payInstallment(installmentId).subscribe({
      next: (response) => {
        this.chargingInstallmentId.set(null);
        this.setLoading.emit(false);

        if (response.data !== true) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Não foi possível realizar a cobrança',
            life: 3000,
          });
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Cobrança enviada com sucesso',
          life: 2000,
        });
        this.reloadOrder.emit();
      },
      error: (e) => {
        this.chargingInstallmentId.set(null);
        this.setLoading.emit(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao cobrar a parcela',
            life: 3000,
          });
        }
      },
    });
  }

  openInstallmentPaymentsDialog(installment: OrderPaymentInstallment): void {
    this.installmentPaymentDialogVisible = true;
    this.paymentInstallment = installment;
  }

  closeInstallmentPaymentsDialog(): void {
    this.installmentPaymentDialogVisible = false;
  }

  getInvoiceStatusDescription(status: InvoiceStatus): string {
    return this.orderService.getInvoiceStatusDescription(status);
  }
}
